import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import CustomRailDialog, {
    CustomIncludeModalConfig,
    CustomIncludeValues,
    RailValidationConfig,
} from "@thkStore/Components/CustomProducts/CustomRailDialog";
import { VariantChildrenGridContextModel } from "@thkStore/Widgets/VariantChildrenGrid/VariantChildrenGridContext";
import React, { useCallback, useMemo, useState } from "react";

function buildValidationConfig(properties: Record<string, string>): RailValidationConfig {
    return {
        minimumLength: parseFloat(properties["minimumLength"]) || 0,
        maximumLength: parseFloat(properties["maximumLength"]) || 0,
        lengthIncrement: parseFloat(properties["lengthIncrement"]) || 0,
        g1Minimum: parseFloat(properties["g1Minimum"]) || 0,
        g1Maximum: parseFloat(properties["g1Maximum"]) || 0,
        g1Increment: parseFloat(properties["g1Increment"]) || 0,
        g2Minimum: parseFloat(properties["g2Minimum"]) || 0,
        g2Maximum: parseFloat(properties["g2Maximum"]) || 0,
        g2Increment: parseFloat(properties["g2Increment"]) || 0,
        holePitchF: parseFloat(properties["holePitchF"]) || 0,
        holeDiameter: parseFloat(properties["holeDiameterD2"]) || 0,
    };
}

interface OwnProps {
    customRailModalConfig: CustomIncludeModalConfig;
}

type Props = OwnProps & HasProductContext;

/**
 * Renders a "Custom Rail" button that opens the CustomRailDialog for custom product variants.
 * On confirmation, the backend-calculated partNo/unitPrice/weight/length/g1/g2/daysToShip are
 * stamped onto the variant's clipboard item so the cart line uses the custom values.
 */
const CustomRailSetupButton: React.FC<Props> = ({ productContext, customRailModalConfig }) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const product = productContext.product;
    const parentProductId =
        (productContext as VariantChildrenGridContextModel).parentProductId ?? product.id;

    const validationConfig = useMemo(
        () => buildValidationConfig(product.properties),
        [product.properties],
    );

    const handleConfirm = useCallback(
        (values: CustomIncludeValues) => {
            setIsDialogOpen(false);
            (productContext as VariantChildrenGridContextModel).onCustomRailConfirmed?.(values);
        },
        [productContext],
    );

    const handleCancel = useCallback(() => {
        setIsDialogOpen(false);
    }, []);

    return (
        <>
            <Button variant="primary" onClick={() => setIsDialogOpen(true)}>
                {translate("Custom Rail")}
            </Button>
            <CustomRailDialog
                isOpen={isDialogOpen}
                config={customRailModalConfig}
                validationConfig={validationConfig}
                customProductTemplateId={product.id}
                parentProductId={parentProductId}
                source="VariantChild"
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            />
        </>
    );
};

export default withProductContext(CustomRailSetupButton);
